import 'babel-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import mixpanel from 'mixpanel-browser';
import { Provider } from 'react-redux';
import { applyRouterMiddleware, Router, browserHistory } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';
import '@seamlesspay/web-ui/primary.css';
import 'antd/dist/antd.css';
import 'react-grid-layout/css/styles.css';

import App from './containers/App';
import { makeSelectLocationState } from './containers/App/selectors';
import LanguageProvider from './containers/LanguageProvider';

import configureStore from './store';
import { translationMessages } from './i18n';
import './global-styles';
import createRoutes from './routes';

import * as Sentry from '@sentry/react';
import { getPortalUrl } from './service/Utils/endpoints';

const isDevelopment = process.env.NODE_ENV === 'development';

const initialState = {};
const store = configureStore(initialState, browserHistory);

const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: makeSelectLocationState()
});
const rootRoute = {
  component: App,
  childRoutes: createRoutes(store)
};

const { portal: portalUrl = 'localhost' } = getPortalUrl() || {};

const MIXPANEL_TOKEN = process.env.MIXPANEL_TOKEN;

mixpanel.init(MIXPANEL_TOKEN, {
  track_pageview: true,
  autocapture: true,
  persistence: 'localStorage'
});

Sentry.init({
  enabled: !isDevelopment,
  dsn: process.env.SENTRY_DSN_NAME,
  integrations: [
    new Sentry.BrowserTracing({
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [portalUrl]
    }),
    new Sentry.Replay()
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
  // Session Replay
  // This sets the sample rate at 10%.
  // You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: 0.1,
  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  replaysOnErrorSampleRate: 1.0
});

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <Router history={history} routes={rootRoute} render={applyRouterMiddleware()} />
      </LanguageProvider>
    </Provider>,
    document.getElementById('app')
  );
};

// Hot reloadable translation json  files
if (import.meta.hot) {
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  import.meta.hot.accept('./i18n', () => {
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en')]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
// if (process.env.NODE_ENV === 'production') {
//   require('offline-plugin/runtime').install(); // eslint-disable-line global-require
// }
